import { writable } from "svelte/store"
import type { Updater } from "svelte/store";
import { deepClone } from "../../utils/obj";
import type {
    ImageFile,
    ImageData,
    Category,
    ChangeableDisplayValue,
    Day,
    OpenHour
} from "./types"

import { DayIndex } from "./types"


class PlaceModel {
    private mainImage: ImageData
    private storeDescription: string
    private hashTags: string[]
    private albumImages: ImageData[] = []
    private categories: Category[]
    private callNumber: string
    private openHours: OpenHour[]
    private breaktime: string
    private update!: (this: void, updater: Updater<PlaceModel>) => void
    private sns: { naver: string, youtube: string, insta: string }
    public constructor(info: ChangeableDisplayValue) {
        this.mainImage = info.mainImage
        this.storeDescription = info.storeDescription
        this.hashTags = info.hashTags
        this.albumImages = [...info.albumImages]
        this.categories = info.categories
        this.callNumber = info.callNumber
        this.openHours = info.openHours
            .sort((a, b) => DayIndex[a.day] - DayIndex[b.day])
        this.breaktime = info.breaktime
        this.sns = {
            naver: info.naverBlog,
            insta: info.instagram,
            youtube: info.youtube
        }
    }

    private newImageData(id: string, src: string | null, edit: ImageFile | null): ImageData {
        return {
            id,
            src,
            edit
        }
    }

    public setUpdater(update: (this: void, updater: Updater<PlaceModel>) => void) {
        this.update = update
    }

    public revokeAllImageURL() {
        if (this.mainImage.src) URL.revokeObjectURL(this.mainImage.src);

        for (const image of this.albumImages) {
            if (image.src) URL.revokeObjectURL(image.src)
        }
    }

    public generatePropertyClone(): ChangeableDisplayValue {
        const clone = {
            mainImage: this.mainImage,
            storeDescription: this.storeDescription,
            hashTags: this.hashTags,
            albumImages: this.albumImages,
            categories: this.categories,
            callNumber: this.callNumber,
            openHours: this.openHours,
            breaktime: this.breaktime,
            naverBlog: this.sns.naver,
            instagram: this.sns.insta,
            youtube: this.sns.youtube
        }
        return Object.assign({}, deepClone(clone), {
            mainImage: clone.mainImage,
            albumImages: clone.albumImages
        })
    }

    public updateMainImage(fileInfo: ImageFile) {
        if (this.mainImage.src) URL.revokeObjectURL(this.mainImage.src);
        if (fileInfo.type === "JPEG") fileInfo.type = "JPG"
        this.mainImage = this.newImageData(
            "",
            URL.createObjectURL(fileInfo.file),
            fileInfo)

        this.update(() => this)
    }

    public deleteMainImage() {
        if (this.mainImage.src) URL.revokeObjectURL(this.mainImage.src);
        this.mainImage = this.newImageData(
            "",
            null,
            null)
        this.update(() => this)
    }

    public getMainImage() {
        return this.mainImage.src || ""
    }

    public getHashTags() {
        return this.hashTags
    }

    public getStoreDescription() {
        return this.storeDescription
    }

    public getAlbumImages() {
        return this.albumImages.map((v) => v.src) as string[]
    }

    public addAlbumImage(fileInfo: ImageFile) {
        if (fileInfo.type === "JPEG") fileInfo.type = "JPG"
        const src = URL.createObjectURL(fileInfo.file);
        this.albumImages.push(this.newImageData("", src, fileInfo));
        this.update(() => this)
    }

    public addHashTag() {
        let newHashTags = [...this.hashTags, ""];
        this.hashTags = newHashTags;
        this.update(() => this)
    }

    public deleteHashTag({ index }: { index: number }) {
        this.hashTags = [
            ...this.hashTags.slice(0, index),
            ...this.hashTags.slice(index + 1, this.hashTags.length)
        ]
        this.update(() => this)
    }

    public updateStoreDescription(storeDescription: string) {
        this.storeDescription = storeDescription
        this.update(() => this)
    }
    public updateAlbumImage(fileInfo: ImageFile & { index: number }) {
        if (fileInfo.type === "JPEG") fileInfo.type = "JPG"
        const original = this.albumImages[fileInfo.index];

        if (original.src) {
            URL.revokeObjectURL(original.src)
        }
        const src = URL.createObjectURL(fileInfo.file);

        this.albumImages[fileInfo.index] = this.newImageData("", src, fileInfo)
        this.update(() => this)
    }

    public deleteAlbumImage(info: { index: number }) {
        const original = this.albumImages[info.index];

        if (original.src) {
            URL.revokeObjectURL(original.src)
        }

        this.albumImages = [
            ...this.albumImages.slice(0, info.index),
            ...this.albumImages.slice(info.index + 1, this.albumImages.length)
        ]
        this.update(() => this)
    }

    public getCategories() {
        return [...this.categories]
    }

    public changeVisibleAllMenus(info: { index: number, visible: boolean }) {
        this.categories[info.index].menus.map((v) => {
            v.show = info.visible;
            return v
        })
        this.update(() => this)
    }

    public changeVisibleOneMenu(info: { index: [number, number] }) {
        const categoryIndex = info.index[0]
        const menuIndex = info.index[1]

        const menu = this.categories[categoryIndex].menus[menuIndex];
        menu.show = !menu.show

        this.update(() => this)
    }

    public updateCallNumber(info: { callNumber: string }) {
        this.callNumber = info.callNumber;
        this.update(() => this)
    }

    public getCallNumber() {
        return this.callNumber
    }

    public getOpenHours() {
        return [...this.openHours]
    }

    public updateDayOff(info: { day: Day, dayOff: boolean }) {
        const { day, dayOff } = info;
        const openhour = this.openHours.find((v) => {
            return v.day === day
        })

        if (!openhour) return;
        const newOpenHour = Object.assign({}, openhour, {
            dayOff
        })

        const replaceTargetIndex = this.openHours.findIndex((v) =>
            v === openhour
        )

        const newOpenHours = [
            ...this.openHours.slice(0, replaceTargetIndex),
            newOpenHour,
            ...this.openHours.slice(replaceTargetIndex + 1, this.openHours.length),
        ]

        this.openHours = newOpenHours.sort((a, b) => DayIndex[a.day] - DayIndex[b.day])
        this.update(() => this)
    }

    public updateOpenHour(info: { day: Day, time: string, isOpen: boolean }) {
        const { day, time } = info;
        const openhour = this.openHours.find((v) => {
            return v.day === day
        })

        let newOpenHour: OpenHour

        if (!openhour) return
        if (!openhour.close) {
            newOpenHour = Object.assign({}, openhour, {
                open: time,
                close: time
            })
        } else {

            newOpenHour = Object.assign({}, openhour, {
                open: time,
            })
        }

        const replaceTargetIndex = this.openHours.findIndex((v) =>
            v === openhour
        )

        const newOpenHours = [
            ...this.openHours.slice(0, replaceTargetIndex),
            newOpenHour,
            ...this.openHours.slice(replaceTargetIndex + 1, this.openHours.length),
        ]

        this.openHours = newOpenHours.sort((a, b) => DayIndex[a.day] - DayIndex[b.day])
        this.update(() => this)
    }

    public updateCloseHour(info: { day: Day, time: string, isOpen: boolean }) {
        const { day } = info;
        let { time } = info;
        const openhour = this.openHours.find((v) => {
            return v.day === day
        })
        let newOpenHour: OpenHour
        if (!openhour) return
        if (!openhour.open) {
            newOpenHour = Object.assign({}, openhour, {
                open: time === "23:59" ? "00:00" : time,
                close: time
            })
        } else {

            newOpenHour = Object.assign({}, openhour, {
                close: time,
            })
        }

        const replaceTargetIndex = this.openHours.findIndex((v) =>
            v === openhour
        )

        const newOpenHours = [
            ...this.openHours.slice(0, replaceTargetIndex),
            newOpenHour,
            ...this.openHours.slice(replaceTargetIndex + 1, this.openHours.length),
        ]

        this.openHours = newOpenHours.sort((a, b) => DayIndex[a.day] - DayIndex[b.day])
        this.update(() => this)
    }

    public updateBreakTime(info: { html: string }) {
        this.breaktime = info.html
        this.update(() => this)
    }

    public getBreakTime() {
        return this.breaktime
    }

    public getSNS(sns: "insta" | "naver" | "youtube") {
        return this.sns[sns]
    }
    public updateSNS(info: { type: "insta" | "naver" | "youtube", value: string }) {
        const { type, value } = info
        this.sns[type] = value
        this.update(() => this)
    }
}

function createPlaceVM(info: ChangeableDisplayValue) {
    const model = new PlaceModel(info)
    const { subscribe, set, update } = writable(model);
    model.setUpdater(update)

    return {
        subscribe,
        set
    }
}
export type SubscribedVM = SvelteStore<PlaceModel>
export default createPlaceVM